import {useMemo, useState} from 'react';
import type {UnitEconomics} from '@ozon-ai/ozon/contracts';
import {Pie, PieChart} from 'recharts';
import {
	type ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from '@/components/ui/chart.tsx';
import type {UnitEconomicsAmountMode} from '../types';
import {formatFlow} from '../format-money';
import {AmountModeToggle} from './amount-mode-toggle';
import {netBillableUnits, scaleAmount} from './unit-economics-amount';

type ChartSegmentKey =
	| 'compensation'
	| 'logistics'
	| 'promotion'
	| 'cogs'
	| 'tax'
	| 'netProfit';

/** Порядок сегментов по часовой стрелке от 12:00. */
const SEGMENT_ORDER: ChartSegmentKey[] = [
	'compensation',
	'logistics',
	'promotion',
	'cogs',
	'tax',
	'netProfit',
];

type ChartSegment = {
	segmentKey: ChartSegmentKey;
	value: number;
	signedTotal: number;
	fill: string;
};

const chartConfig = {
	compensation: {
		label: 'Вознаграждение',
		color: 'var(--chart-1)',
	},
	logistics: {
		label: 'Логистика',
		color: 'var(--chart-2)',
	},
	promotion: {
		label: 'Продвижение и реклама',
		color: 'var(--chart-3)',
	},
	cogs: {
		label: 'Себестоимость',
		color: 'var(--chart-4)',
	},
	tax: {
		label: 'Налог',
		color: 'var(--chart-5)',
	},
	netProfit: {
		label: 'Чистая прибыль',
		color: 'oklch(0.627 0.194 149.214)',
	},
} satisfies ChartConfig;

function expenseMagnitude(value: number | null | undefined): number {
	if (value == null || value === 0) return 0;
	return Math.abs(value);
}

function segmentSignedTotal(row: UnitEconomics, segmentKey: ChartSegmentKey): number | null {
	switch (segmentKey) {
		case 'compensation':
			return row.reward + row.acquiring;
		case 'logistics':
			return row.delivery.logistics + row.delivery.lastMile;
		case 'promotion':
			return row.promotion !== 0 ? row.promotion : null;
		case 'cogs':
			return row.cogs;
		case 'tax':
			return row.tax;
		case 'netProfit':
			return row.netProfit != null && row.netProfit !== 0 ? row.netProfit : null;
	}
}

function buildSegments(
	row: UnitEconomics,
	mode: UnitEconomicsAmountMode,
): ChartSegment[] {
	const netUnits = netBillableUnits(row);
	const items: Omit<ChartSegment, 'fill'>[] = [];

	for (const segmentKey of SEGMENT_ORDER) {
		const signedTotal = segmentSignedTotal(row, segmentKey);
		if (signedTotal == null) continue;

		const scaled = scaleAmount(signedTotal, mode, netUnits);
		if (scaled == null || scaled === 0) continue;

		const magnitude = expenseMagnitude(scaled);
		if (magnitude <= 0) continue;

		items.push({
			segmentKey,
			value: magnitude,
			signedTotal: scaled,
		});
	}

	return items.map((item) => ({
		...item,
		fill: `var(--color-${item.segmentKey})`,
	}));
}

const CHART_SIZE_PX = 220;
const LARGE_REVENUE_THRESHOLD_RUB = 1_000_000;
const LARGE_RING_SCALE = 1.1;

type UnitEconomicsRadialChartProps = {
	row: UnitEconomics;
	/** Только суммы за период, без переключателя «на 1 шт» */
	periodTotalsOnly?: boolean;
};

export function UnitEconomicsRadialChart({row, periodTotalsOnly = false}: UnitEconomicsRadialChartProps) {
	const [amountMode, setAmountMode] = useState<UnitEconomicsAmountMode>(
		periodTotalsOnly ? 'period' : 'perUnit',
	);
	const resolvedMode = periodTotalsOnly ? 'period' : amountMode;
	const segments = useMemo(() => buildSegments(row, resolvedMode), [row, resolvedMode]);
	const centerRevenue = useMemo(
		() => scaleAmount(row.sales.total, resolvedMode, netBillableUnits(row)),
		[row, resolvedMode],
	);
	const total = useMemo(
		() => segments.reduce((sum, segment) => sum + segment.value, 0),
		[segments],
	);
	const largeRing = row.sales.total > LARGE_REVENUE_THRESHOLD_RUB;
	const chartSizePx = Math.round(CHART_SIZE_PX * (largeRing ? LARGE_RING_SCALE : 1));
	const innerRadiusPct = 58 * (largeRing ? LARGE_RING_SCALE : 1);
	const outerRadiusPct = 82 * (largeRing ? LARGE_RING_SCALE : 1);

	return (
		<div className="flex flex-col gap-3">
			{periodTotalsOnly ? null : (
				<AmountModeToggle value={amountMode} onChange={setAmountMode}/>
			)}

			{segments.length === 0 || total <= 0 ? (
				<p className="text-center text-sm text-muted-foreground">
					Недостаточно данных для диаграммы
				</p>
			) : (
				<>
					<div
						className="relative mx-auto aspect-square size-full"
						style={{maxHeight: chartSizePx, maxWidth: chartSizePx}}
					>
						<div className="pointer-events-none absolute inset-0 z-0 flex flex-col items-center justify-center text-center">
							<span className="text-lg font-semibold tabular-nums text-foreground">
								{formatFlow(centerRevenue)}
							</span>
							<span className="text-xs text-muted-foreground">выручка</span>
						</div>
						<ChartContainer
							config={chartConfig}
							className="relative z-10 aspect-square size-full"
							initialDimension={{width: chartSizePx, height: chartSizePx}}
						>
							<PieChart margin={{top: 0, right: 0, bottom: 0, left: 0}}>
								<ChartTooltip
									content={
										<ChartTooltipContent
											hideLabel
											formatter={(value, _name, item) => {
												const segment = item.payload as ChartSegment;
												const numeric = typeof value === 'number' ? value : 0;
												const share = total > 0 ? (numeric / total) * 100 : 0;
												const asExpense = segment.segmentKey !== 'netProfit';
												return (
													<div className="flex w-full flex-col gap-0.5">
														<span className="font-medium">
															{chartConfig[segment.segmentKey].label}
														</span>
														<span className="tabular-nums">
															{formatFlow(segment.signedTotal, asExpense)}
														</span>
														<span className="text-muted-foreground tabular-nums">
															{share.toFixed(1)}%
														</span>
													</div>
												);
											}}
										/>
									}
								/>
								<Pie
									data={segments}
									dataKey="value"
									nameKey="segmentKey"
									cx="50%"
									cy="50%"
									innerRadius={`${innerRadiusPct}%`}
									outerRadius={`${outerRadiusPct}%`}
									startAngle={90}
									endAngle={-270}
									strokeWidth={2}
									stroke="var(--background)"
								/>
							</PieChart>
						</ChartContainer>
					</div>
					<div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs">
						{segments.map((segment) => (
							<div key={segment.segmentKey} className="flex items-center gap-1.5">
								<div
									className="size-2 shrink-0 rounded-[2px]"
									style={{backgroundColor: chartConfig[segment.segmentKey].color}}
								/>
								<span>{chartConfig[segment.segmentKey].label}</span>
							</div>
						))}
					</div>
				</>
			)}
		</div>
	);
}
